import React from 'react';

/**
 * Top-level React error boundary.
 *
 * Catches render-time exceptions anywhere below it in the tree and
 * shows a recoverable fallback UI instead of leaving the whole tab as
 * a white screen. Logs the error to the console with a stable tag so
 * it shows up in any browser-side logging pipeline (Sentry, DevTools,
 * Playwright traces, etc.).
 *
 * React function components can't implement error boundaries today —
 * this class component pattern is the only supported path.
 *
 * Ported from PitchPilot main PR #109 (v2.32.0). Source of truth for
 * this behavior lives in PP main.
 */
interface State {
  error: Error | null;
  componentStack: string | null;
}

interface Props {
  children: React.ReactNode;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ componentStack: info.componentStack || null });
    // Stable tag so anyone grepping browser logs can find these fast.
    console.error('[pp-error-boundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  reload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    const stack = this.state.error.stack || '';
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 24, gap: 16, background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #000)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Something went wrong.</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary, #555)', maxWidth: 560, textAlign: 'center' }}>
          The page hit a render error and stopped. You can try recovering in place,
          or reload the page. The full error is in your browser console under the
          tag <code>[pp-error-boundary]</code>.
        </div>
        <pre style={{
          maxWidth: 720, maxHeight: 200, overflow: 'auto',
          padding: 12, background: 'var(--bg-surface, #f5f5f5)',
          border: '1px solid var(--border, #ddd)', borderRadius: 6,
          fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {this.state.error.message}
          {stack ? `\n\n${stack.split('\n').slice(0, 6).join('\n')}` : ''}
        </pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={this.reset} style={{
            padding: '8px 16px', fontSize: 14, cursor: 'pointer',
            background: 'transparent', border: '1px solid var(--border, #ddd)', borderRadius: 6,
            color: 'var(--text-primary, #000)',
          }}>Try again</button>
          <button onClick={this.reload} style={{
            padding: '8px 16px', fontSize: 14, cursor: 'pointer',
            background: 'var(--brand, #0033FF)', border: 'none', borderRadius: 6, color: '#fff',
          }}>Reload page</button>
        </div>
      </div>
    );
  }
}
